import { WidgetIOSchema } from '../types/index.js';

// Define colors for different data types - functional and minimal
export const TYPE_COLORS = {
  // Primitives
  string: '#9E9E9E',      // Grey - neutral for text
  number: '#007AFF',      // Blue - for numeric data
  boolean: '#FF9500',     // Orange - for binary states
  
  // Complex types
  object: '#7406F2',      // Brand purple - for complex data (used sparingly)
  array: '#616161',       // Dark grey - for collections
  
  // Special types
  any: '#424242',         // Darker grey - for untyped
  
  // Domain-specific formats
  'stock-symbol': '#FFFFFF',  // White - high importance
  'percentage': '#34C759',    // Green - for growth/positive
} as const;

// Get color for a schema type
export function getTypeColor(schema?: WidgetIOSchema): string {
  if (!schema) return TYPE_COLORS.any;
  
  // Check for special formats first
  if (schema.format === 'stock-symbol') return TYPE_COLORS['stock-symbol'];
  if (schema.format === 'percentage') return TYPE_COLORS.percentage;
  
  // Check if it's an enum (special case)
  if (schema.constraints?.enum) {
    return TYPE_COLORS.string; // Enums are usually strings
  }
  
  // Return color based on base type
  const baseType = schema.type;
  return TYPE_COLORS[baseType as keyof typeof TYPE_COLORS] || TYPE_COLORS.any;
}

// Check if two types are compatible for connection
export function areTypesCompatible(outputSchema?: WidgetIOSchema, inputSchema?: WidgetIOSchema): boolean {
  // If either is 'any', they're compatible
  if (!outputSchema || !inputSchema) return true;
  if (outputSchema.type === 'any' || inputSchema.type === 'any') return true;
  
  // Same base type
  if (outputSchema.type !== inputSchema.type) return false;
  
  // For objects, check if output has all required input properties
  if (outputSchema.type === 'object' && inputSchema.type === 'object') {
    const outputProps = outputSchema.properties || {};
    const inputProps = inputSchema.properties || {};
    
    // Check if output has all properties that input requires
    for (const [key, prop] of Object.entries(inputProps)) {
      if (!outputProps[key]) return false;
      // Recursively check nested properties
      if (!areTypesCompatible(outputProps[key], prop)) return false;
    }
    return true;
  }
  
  // For arrays, check item types
  if (outputSchema.type === 'array' && inputSchema.type === 'array') {
    return areTypesCompatible(outputSchema.items, inputSchema.items);
  }
  
  return true;
}